import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const DISC_LABELS = {
  muay_thai_kickboxing: "🥊 Muay-Thai / Kick",
  jiu_jitsu_bresilien: "🥋 BJJ (Brésilien)",
  jiu_jitsu_can_ryu: "🥋 Jiu-jitsu Can Ryu",
  petits_samourais: "👦 Petits Samouraïs",
  powerkick_her: "💃 PowerKick Her",
  drop_in: "🎫 Drop-In",
  inscription: "📝 Inscription",
  cours_prive: "👤 Cours privés",
  evaluation: "📊 Évaluation",
  custom: "⚙️ Custom",
  karate_NON_OFFERT: "❌ Karaté (HORS SCOPE)",
  autre: "🔹 Autre / Boutique",
};

export default function ZenPlannerMigrationModule() {
  const [summary, setSummary] = useState(null);
  const [tarifs, setTarifs] = useState([]);
  const [tarifsByDisc, setTarifsByDisc] = useState({});
  const [discFilter, setDiscFilter] = useState("");
  const [people, setPeople] = useState([]);
  const [blockedCount, setBlockedCount] = useState(0);
  const [unmatched, setUnmatched] = useState([]);
  const [tab, setTab] = useState("tarifs");
  const [transition, setTransition] = useState(null);
  const [transitionFilter, setTransitionFilter] = useState("");
  const [transitionSearch, setTransitionSearch] = useState("");
  const [sending, setSending] = useState({});
  const [previewModal, setPreviewModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = async () => {
    const s = await api.get("/zp-migration/summary");
    setSummary(s.data);
  };
  const fetchTarifs = async (disc = discFilter) => {
    const q = disc ? `?discipline=${disc}` : "";
    const { data } = await api.get(`/zp-migration/tarifs${q}`);
    setTarifs(data.tarifs || []);
    setTarifsByDisc(data.by_discipline || {});
  };
  const fetchUnmatched = async () => {
    const { data } = await api.get("/zp-migration/unmatched-kaizen");
    setUnmatched(data.unmatched || []);
  };
  const fetchPeople = async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.append("status", statusFilter);
    if (search) params.append("search", search);
    params.append("limit", "300");
    const { data } = await api.get(`/zp-migration/people?${params.toString()}`);
    setPeople(data.people || []);
    setBlockedCount(data.blocked_count || 0);
  };
  const fetchTransition = async () => {
    const { data } = await api.get("/zp-migration/transition/dashboard");
    setTransition(data);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([fetchSummary(), fetchTarifs(""), fetchUnmatched()]);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (tab === "people") fetchPeople();
    if (tab === "transition") fetchTransition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter]);

  useEffect(() => {
    if (tab === "tarifs") fetchTarifs(discFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discFilter]);

  const doImportLeads = async () => {
    if (!window.confirm(`Importer Alumni + Prospects (hors ${blockedCount} bloqués) comme leads CRM ?`)) return;
    setImporting(true);
    try {
      const { data } = await api.post("/zp-migration/import-as-leads?statuses=Alumni,PROSPECT");
      setImportResult(data);
      await fetchSummary();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setImporting(false);
    }
  };

  const blockPerson = async (name) => {
    if (!window.confirm(`Marquer "${name}" comme "Ne plus contacter" ?`)) return;
    try {
      await api.post(`/zp-migration/people/${encodeURIComponent(name)}/block`);
      await fetchPeople();
    } catch (e) {
      setError(e.message);
    }
  };
  const deletePerson = async (name) => {
    if (!window.confirm(`SUPPRIMER DÉFINITIVEMENT "${name}" de la base ?`)) return;
    try {
      await api.delete(`/zp-migration/people/${encodeURIComponent(name)}`);
      await fetchPeople();
      await fetchSummary();
    } catch (e) {
      setError(e.message);
    }
  };

  const previewTransition = async (cid) => {
    try {
      const { data } = await api.post(`/zp-migration/transition/preview/${cid}`);
      setPreviewModal(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur preview");
    }
  };
  const sendTransitionEmail = async (cid) => {
    if (!window.confirm("Envoyer le courriel de transition à ce membre ?")) return;
    setSending((s) => ({ ...s, [cid]: true }));
    try {
      await api.post(`/zp-migration/transition/send-email/${cid}`, {});
      toast.success("📧 Courriel envoyé");
      await fetchTransition();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi (vérifie Gmail connecté)");
    } finally {
      setSending((s) => { const n = { ...s }; delete n[cid]; return n; });
    }
  };
  const sendPaymentLink = async (cid) => {
    if (!window.confirm("Créer l'abonnement Stripe et envoyer le lien de paiement par courriel à cet élève ?")) return;
    setSending((s) => ({ ...s, [cid]: true }));
    try {
      const { data } = await api.post(`/zp-migration/transition/send-payment-link/${cid}`, { origin_url: window.location.origin });
      if (data.email_sent) toast.success("💳 Lien de paiement envoyé par courriel");
      else toast.warning(data.warning || "Lien créé (courriel non envoyé — vérifie Gmail)");
      await fetchTransition();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création du lien Stripe");
    } finally {
      setSending((s) => { const n = { ...s }; delete n[cid]; return n; });
    }
  };

  const setTransitionStatus = async (cid, status) => {
    try {
      await api.patch(`/zp-migration/transition/${cid}/status`, { status });
      toast.success(`Statut → ${status}`);
      await fetchTransition();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const sendBulkTransition = async () => {
    if (selectedIds.size === 0) { toast.error("Sélectionne au moins un contrat"); return; }
    if (!window.confirm(`Envoyer le courriel de transition à ${selectedIds.size} membre(s) ?`)) return;
    try {
      const { data } = await api.post(`/zp-migration/transition/send-bulk`, { contract_ids: Array.from(selectedIds) });
      toast.success(`📧 ${data.sent} envoyés · ${data.skipped} skippés · ${data.errors?.length || 0} erreurs`);
      setSelectedIds(new Set());
      await fetchTransition();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi en lot");
    }
  };

  if (loading) return <div className="text-zinc-400">Chargement migration ZP…</div>;

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="zp-migration-module">
      <PageHeader
        title="Migration Zen Planner → Kaizen"
        icon={ArrowsClockwise}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Migration Zen Planner" }]}
        subtitle="7 ans d'historique extrait · 7965 paiements · 771 tarifs uniques · 927 personnes"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Personnes</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{summary?.zp_people_total ?? 0}</div>
        </div>
        <div className="bg-zinc-900 border border-emerald-700/40 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Membres actifs</div>
          <div className="text-2xl font-bold text-emerald-400 mt-1">
            {summary?.zp_people_by_status?.STUDENT ?? 0}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Alumni</div>
          <div className="text-2xl font-bold text-blue-300 mt-1">
            {summary?.zp_people_by_status?.Alumni ?? 0}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Tarifs uniques</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{summary?.zp_tarifs_total ?? 0}</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Paiements 2019-26</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{summary?.zp_payments_total ?? 0}</div>
        </div>
      </div>

      {/* Action: Import alumni/prospects */}
      <div className="bg-gradient-to-r from-amber-500/10 to-emerald-500/10 border border-amber-500/30 rounded-lg p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <div className="text-lg font-semibold text-amber-300">
              💡 Campagne réactivation alumni
            </div>
            <div className="text-sm text-zinc-300 mt-1">
              Importer <strong>{summary?.zp_people_by_status?.Alumni ?? 0} alumni</strong> +{" "}
              <strong>{summary?.zp_people_by_status?.PROSPECT ?? 0} prospects</strong> comme leads
              {blockedCount > 0 && (
                <span className="text-rose-400"> · {blockedCount} bloqués (Ne plus contacter)</span>
              )}
            </div>
            {importResult && (
              <div className="text-xs text-emerald-300 mt-2">
                ✓ Importés: {importResult.imported} · Doublons ignorés: {importResult.skipped_duplicates}
              </div>
            )}
          </div>
          <button
            data-testid="zp-import-leads-btn"
            onClick={doImportLeads}
            disabled={importing}
            className="px-4 py-2 rounded-md bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition disabled:opacity-50"
          >
            {importing ? "Import…" : "🚀 Importer dans CRM"}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/40 text-rose-300 p-3 rounded">⚠️ {error}</div>
      )}

      {/* TABS */}
      <div className="flex gap-2 border-b border-zinc-800 overflow-x-auto">
        {[
          ["tarifs", `💰 Tarifs (${summary?.zp_tarifs_total ?? 0})`],
          ["people", `👥 Personnes (${summary?.zp_people_total ?? 0})`],
          ["unmatched", `⚠️ Non matchés (${unmatched.length})`],
          ["transition", `🔄 Transition Stripe`],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`zp-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition ${
              tab === k
                ? "text-amber-400 border-b-2 border-amber-500"
                : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* TARIFS TAB */}
      {tab === "tarifs" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setDiscFilter("")}
              data-testid="disc-filter-all"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                discFilter === ""
                  ? "bg-amber-500 text-zinc-950"
                  : "bg-zinc-900 border border-zinc-800 text-zinc-300"
              }`}
            >
              Toutes ({summary?.zp_tarifs_total ?? 0})
            </button>
            {Object.entries(tarifsByDisc)
              .sort((a, b) => b[1] - a[1])
              .map(([d, count]) => (
                <button
                  key={d}
                  onClick={() => setDiscFilter(d)}
                  data-testid={`disc-filter-${d}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                    discFilter === d
                      ? "bg-amber-500 text-zinc-950"
                      : "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-amber-500/40"
                  }`}
                >
                  {DISC_LABELS[d] || d} ({count})
                </button>
              ))}
          </div>
          <div className="text-xs text-zinc-500">
            Triés par fréquence d'achat — prix médian = ce que la majorité paye vraiment (7 ans d'historique)
          </div>
          <div className="space-y-1 max-h-[700px] overflow-y-auto">
            {tarifs.map((t, i) => (
              <div
                key={i}
                className="bg-zinc-900 border border-zinc-800 rounded p-3 hover:border-amber-500/30 transition flex items-center justify-between gap-3"
                data-testid={`zp-tarif-row-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-100 truncate">{t.name}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {DISC_LABELS[t.discipline_guess] || t.discipline_guess} · {t.count}× achats ·{" "}
                    {(t.years_active || []).slice(-3).join(", ")}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-bold text-amber-300">{t.median_price?.toFixed(2)}$</div>
                  <div className="text-[10px] text-zinc-500">
                    {t.min_price?.toFixed(0)}-{t.max_price?.toFixed(0)}$
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PEOPLE TAB */}
      {tab === "people" && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            {["", "STUDENT", "Alumni", "PROSPECT", "STAFF"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`zp-status-${s || "all"}`}
                className={`px-3 py-1.5 rounded-md text-sm transition ${
                  statusFilter === s
                    ? "bg-amber-500 text-zinc-950 font-semibold"
                    : "bg-zinc-900 border border-zinc-800 text-zinc-300"
                }`}
              >
                {s || "Tous"}
              </button>
            ))}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchPeople()}
              placeholder="🔍 Recherche nom/email/phone…"
              data-testid="zp-search-input"
              className="flex-1 min-w-[200px] px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-200"
            />
            <button
              onClick={fetchPeople}
              data-testid="zp-search-btn"
              className="px-3 py-1.5 rounded-md bg-amber-500 text-zinc-950 text-sm font-semibold"
            >
              Chercher
            </button>
          </div>
          {blockedCount > 0 && (
            <div className="text-xs text-rose-400">
              🚫 {blockedCount} personnes bloquées (cachées de la liste et exclues des campagnes)
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-2 max-h-[600px] overflow-y-auto">
            {people.map((p, i) => (
              <div
                key={i}
                className="bg-zinc-900 border border-zinc-800 rounded p-3 text-sm hover:border-amber-500/30"
                data-testid={`zp-person-${i}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-100">{p.name}</div>
                    <div className="text-xs text-zinc-400 mt-1">
                      📧 {p.email || "—"} · 📱 {p.phone || "—"}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] uppercase px-2 py-0.5 rounded shrink-0 ${
                      p.zp_status === "STUDENT"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : p.zp_status === "Alumni"
                        ? "bg-blue-500/20 text-blue-300"
                        : p.zp_status === "PROSPECT"
                        ? "bg-amber-500/20 text-amber-300"
                        : "bg-zinc-700 text-zinc-300"
                    }`}
                  >
                    {p.zp_status}
                  </span>
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => blockPerson(p.name)}
                    data-testid={`block-${i}`}
                    className="text-[11px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-300 border border-rose-500/30 hover:bg-rose-500/20"
                  >
                    🚫 Ne plus contacter
                  </button>
                  <button
                    onClick={() => deletePerson(p.name)}
                    data-testid={`delete-${i}`}
                    className="text-[11px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-rose-700"
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* UNMATCHED TAB */}
      {tab === "unmatched" && (
        <div className="space-y-2">
          <div className="text-xs text-zinc-500">
            Contrats Kaizen sans correspondance ZP exacte (probablement orthographe différente)
          </div>
          {unmatched.map((c, i) => (
            <div key={i} className="bg-zinc-900 border border-rose-500/30 rounded p-3 text-sm">
              <div className="font-semibold text-zinc-100">{c.full_name}</div>
              <div className="text-xs text-zinc-400 mt-1">
                {c.email || "—"} · {c.phone || "—"} · {c.discipline} · {c.statut}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TRANSITION STRIPE TAB */}
      {tab === "transition" && transition && (
        <div className="space-y-4" data-testid="transition-tab">
          {/* Counts */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["not_started", "⏳ À envoyer", "amber-400"],
              ["email_sent", "📧 Envoyé", "blue-400"],
              ["acknowledged", "👍 Reçu/accepté", "violet-400"],
              ["billing_active", "✅ Facturation active", "emerald-400"],
              ["declined", "❌ Décliné", "rose-400"],
            ].map(([k, label, color]) => (
              <div key={k} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3" data-testid={`transition-count-${k}`}>
                <div className="text-xs uppercase text-zinc-500">{label}</div>
                <div className={`text-2xl font-bold text-${color} mt-1`}>
                  {transition.counts[k] || 0}
                </div>
              </div>
            ))}
          </div>

          {/* 🚦 PRÊT AU TRANSFERT — contrat actif + abonnement Stripe relié */}
          {transition.readiness && (
            <div className="grid grid-cols-3 gap-3" data-testid="transfer-readiness">
              <div className="bg-emerald-500/10 border border-emerald-700/40 rounded-lg p-3" data-testid="readiness-ready">
                <div className="text-xs uppercase text-emerald-300/70">✅ Prêts au transfert</div>
                <div className="text-2xl font-bold text-emerald-400 mt-1">{transition.readiness.ready}</div>
                <div className="text-[11px] text-zinc-500 mt-1">Actif + Stripe relié</div>
              </div>
              <div className="bg-amber-500/10 border border-amber-700/40 rounded-lg p-3" data-testid="readiness-missing-stripe">
                <div className="text-xs uppercase text-amber-300/70">⚠️ Sans abonnement Stripe</div>
                <div className="text-2xl font-bold text-amber-400 mt-1">{transition.readiness.missing_stripe}</div>
                <div className="text-[11px] text-zinc-500 mt-1">À relier avant facturation</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3" data-testid="readiness-not-ready">
                <div className="text-xs uppercase text-zinc-500">Pas encore prêts</div>
                <div className="text-2xl font-bold text-zinc-300 mt-1">{transition.readiness.not_ready}</div>
                <div className="text-[11px] text-zinc-500 mt-1">sur {transition.total} total</div>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="🔍 Recherche nom/email…"
              value={transitionSearch}
              onChange={(e) => setTransitionSearch(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm flex-1 min-w-[200px]"
              data-testid="transition-search"
            />
            <select
              value={transitionFilter}
              onChange={(e) => setTransitionFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm"
              data-testid="transition-filter"
            >
              <option value="">Tous statuts</option>
              <option value="not_started">À envoyer</option>
              <option value="email_sent">Envoyés</option>
              <option value="acknowledged">Acceptés</option>
              <option value="billing_active">Facturation active</option>
              <option value="declined">Déclinés</option>
            </select>
            <button
              onClick={sendBulkTransition}
              disabled={selectedIds.size === 0}
              className="px-4 py-1.5 rounded bg-amber-500 text-zinc-950 text-sm font-semibold hover:bg-amber-400 disabled:opacity-30"
              data-testid="transition-bulk-send"
            >
              📧 Envoyer en lot ({selectedIds.size})
            </button>
          </div>

          {/* Contracts list */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          const eligible = (transition.contracts || []).filter(
                            (c) =>
                              (c.zp_transition_status === "not_started") &&
                              (!transitionFilter || c.zp_transition_status === transitionFilter) &&
                              (!transitionSearch || (c.full_name + c.email).toLowerCase().includes(transitionSearch.toLowerCase()))
                          );
                          setSelectedIds(new Set(eligible.map((c) => c.id)));
                        } else setSelectedIds(new Set());
                      }}
                      data-testid="transition-select-all"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Membre</th>
                  <th className="px-3 py-2 text-left">Discipline</th>
                  <th className="px-3 py-2 text-right">Tarif/mois</th>
                  <th className="px-3 py-2 text-center">Prêt transfert</th>
                  <th className="px-3 py-2 text-center">Statut transition</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(transition.contracts || [])
                  .filter((c) => !transitionFilter || c.zp_transition_status === transitionFilter)
                  .filter((c) =>
                    !transitionSearch ||
                    (c.full_name + " " + (c.email || "")).toLowerCase().includes(transitionSearch.toLowerCase())
                  )
                  .map((c) => (
                    <tr key={c.id} className="border-t border-zinc-800" data-testid={`transition-row-${c.id}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          disabled={c.zp_transition_status !== "not_started"}
                          onChange={(e) => {
                            const n = new Set(selectedIds);
                            if (e.target.checked) n.add(c.id); else n.delete(c.id);
                            setSelectedIds(n);
                          }}
                          data-testid={`transition-check-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-zinc-100">{c.full_name}</div>
                        <div className="text-xs text-zinc-500">{c.email}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-400">{c.discipline || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-300">
                        {c.tarif_mensuel ? `${c.tarif_mensuel} $` : "—"}
                      </td>
                      <td className="px-3 py-2 text-center" data-testid={`readiness-cell-${c.id}`}>
                        {c.transfer_ready ? (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-semibold" title="Contrat actif + abonnement Stripe relié">✅ Prêt</span>
                        ) : c.has_stripe ? (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-400" title={`Stripe: ${c.stripe_subscription_status || "?"} · statut: ${c.statut}`}>⚠️ {c.statut}</span>
                        ) : (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-400" title="Aucun abonnement Stripe relié">○ Sans Stripe</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <TransitionBadge status={c.zp_transition_status} />
                      </td>
                      <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                        <button
                          onClick={() => previewTransition(c.id)}
                          className="text-xs px-2 py-1 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300"
                          data-testid={`transition-preview-${c.id}`}
                        >
                          👁️
                        </button>
                        {!c.transfer_ready && c.tarif_mensuel > 0 && (
                          <button
                            onClick={() => sendPaymentLink(c.id)}
                            disabled={sending[c.id]}
                            className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded text-white font-semibold disabled:opacity-50"
                            data-testid={`transition-paylink-${c.id}`}
                            title="Crée l'abonnement Stripe + envoie le lien de paiement par courriel"
                          >
                            {sending[c.id] ? "…" : "💳 Lien paiement"}
                          </button>
                        )}
                        {c.zp_transition_status === "not_started" && (
                          <button
                            onClick={() => sendTransitionEmail(c.id)}
                            disabled={sending[c.id]}
                            className="text-xs px-2 py-1 bg-amber-500 hover:bg-amber-400 rounded text-zinc-950 font-semibold disabled:opacity-50"
                            data-testid={`transition-send-${c.id}`}
                          >
                            {sending[c.id] ? "…" : "📧 Envoyer"}
                          </button>
                        )}
                        {c.zp_transition_status === "email_sent" && (
                          <>
                            <button
                              onClick={() => setTransitionStatus(c.id, "billing_active")}
                              className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded text-white"
                              data-testid={`transition-activate-${c.id}`}
                            >
                              ✅ Activé
                            </button>
                            <button
                              onClick={() => setTransitionStatus(c.id, "declined")}
                              className="text-xs px-2 py-1 bg-rose-600 hover:bg-rose-500 rounded text-white"
                              data-testid={`transition-decline-${c.id}`}
                            >
                              ❌ Décliné
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="transition-preview-modal">
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col p-6">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-xs text-amber-400 uppercase">— Aperçu du courriel</div>
                <h3 className="text-xl font-bold mt-1">À : {previewModal.to}</h3>
                <div className="text-sm text-zinc-400 mt-1">Sujet : {previewModal.subject}</div>
              </div>
              <button onClick={() => setPreviewModal(null)} className="text-zinc-400 hover:text-zinc-100 text-2xl">×</button>
            </div>
            <pre className="flex-1 overflow-y-auto bg-zinc-900 border border-zinc-800 rounded p-4 text-sm whitespace-pre-wrap text-zinc-200">
              {previewModal.body}
            </pre>
            <div className="text-xs text-zinc-500 mt-2">
              Tip : tu peux personnaliser ce template dans <code>/app/backend/routes/zp_migration.py</code> · variable <code>_EMAIL_TEMPLATE_BODY</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TransitionBadge({ status }) {
  const map = {
    not_started: ["⏳ À envoyer", "bg-amber-500/20 text-amber-300 border-amber-500/40"],
    email_sent: ["📧 Envoyé", "bg-blue-500/20 text-blue-300 border-blue-500/40"],
    acknowledged: ["👍 Accepté", "bg-violet-500/20 text-violet-300 border-violet-500/40"],
    billing_active: ["✅ Actif", "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"],
    declined: ["❌ Décliné", "bg-rose-500/20 text-rose-300 border-rose-500/40"],
  };
  const [label, cls] = map[status] || ["?", "bg-zinc-800 text-zinc-300 border-zinc-700"];
  return (
    <span className={`text-xs px-2 py-1 rounded border ${cls} font-semibold whitespace-nowrap`}>
      {label}
    </span>
  );
}

